import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Chip } from '@mui/material';
import i18n from '../../../i18n';
import { AppDataContext } from '../../../context/Context';
import { getExtractValue } from '../../../utils/formatName';
import Iconify from '../../../components/iconify';

// Applied filters used to be invisible: the controls that set them scroll out of
// view, and nothing on the page said why a grid was showing 12 rows instead of
// 4,000. These chips are that missing readout, and the only place a filter can
// be removed one at a time.

/**
 * Filter keys a Head entry can end up stored under. The filter controls disagree
 * about this — FilterItemsField writes `field.id`, FilterItemsContextField
 * writes `field.id`, and FilterContextField derives `role.id` from `roleId` —
 * so a chip has to accept any of them to find its column.
 */
const candidateKeys = (field) => {
    const keys = [field.id, field.filterBy].filter(Boolean);
    const filterField = field.filterField || field.id;
    if (filterField) {
        keys.push(
            filterField.endsWith('Id')
                ? `${filterField.slice(0, -2)}.id`
                : `${filterField}.id`
        );
    }
    return keys;
};

class ActiveFilterChips extends React.Component {
    static contextType = AppDataContext;

    findField(filterKey) {
        return (this.props.Head || []).find(
            (field) =>
                field.filterCell && candidateKeys(field).includes(filterKey)
        );
    }

    /** Human-readable rendering of a filter's selected values. */
    describeValues(field, values) {
        if (!Array.isArray(values)) return String(values ?? '');

        // Beyond a couple of values the names stop fitting; the count is more
        // useful than a truncated list.
        if (values.length > 2) {
            return i18n.t('common.nSelected', { count: values.length });
        }

        return values
            .map((value) => this.describeValue(field, value))
            .join(', ');
    }

    describeValue(field, value) {
        if (!field) return String(value);

        // Static option lists carry their own translation namespace.
        if (Array.isArray(field.items) && field.items.includes(value)) {
            return field.translate
                ? i18n.t(`${field.translate}.${value}`)
                : String(value);
        }

        // Server-backed options: the same store the filter control reads from.
        const options = field.filterBy
            ? this.context.getState().controllersData?.[field.filterBy]
            : null;
        const match = Array.isArray(options)
            ? options.find((option) => option.id === value)
            : null;

        if (match) {
            return (
                getExtractValue(
                    match,
                    field.show,
                    field.nestedFilterObject,
                    field.secondaryFilterObject
                ) || String(value)
            );
        }

        return String(value);
    }

    label(field, filter) {
        const columnKey = field ? field.labelCell || field.label : null;
        const name = columnKey ? i18n.t(`columns.${columnKey}`) : filter.field;
        return `${name}: ${this.describeValues(field, filter.value)}`;
    }

    render() {
        const { filters, onChange, showingArchived, onShowActive } = this.props;
        const active = (filters || []).filter(
            (filter) =>
                filter &&
                filter.value !== undefined &&
                filter.value !== null &&
                (!Array.isArray(filter.value) || filter.value.length > 0)
        );

        if (!active.length && !showingArchived) return null;

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    pb: 1.5
                }}
            >
                {/* Not a filter the user set, so not in the list and not
                    cleared by "Clear all"; removing it goes back to active rows. */}
                {showingArchived && (
                    <Chip
                        size="small"
                        color="warning"
                        icon={<Iconify icon="eva:archive-outline" width={16} />}
                        label={i18n.t('common.showingArchived')}
                        onDelete={onShowActive}
                    />
                )}

                {active.map((filter) => {
                    const field = this.findField(filter.field);
                    return (
                        <Chip
                            key={filter.field}
                            size="small"
                            variant="outlined"
                            label={this.label(field, filter)}
                            onDelete={() =>
                                onChange(
                                    active.filter(
                                        (other) => other.field !== filter.field
                                    )
                                )
                            }
                        />
                    );
                })}

                {active.length > 1 && (
                    <Button size="small" onClick={() => onChange([])}>
                        {i18n.t('common.clearAllFilters')}
                    </Button>
                )}
            </Box>
        );
    }
}

ActiveFilterChips.propTypes = {
    /** Grid head, used to turn a filter key back into a column name. */
    Head: PropTypes.array,
    /** Flat filter list, as stored by FilterService. */
    filters: PropTypes.array,
    /** Receives the filters that should remain. */
    onChange: PropTypes.func.isRequired,
    /** The grid lists archived rows; shown as a chip of its own. */
    showingArchived: PropTypes.bool,
    /** Leaves the archived view. */
    onShowActive: PropTypes.func
};

export default ActiveFilterChips;
